import {HttpClient, HttpClient$, PublicHttpClient$, UploadClient, UploadClient$} from '@raison/network-facade';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {Provider as JotaiProvider, type createStore} from 'jotai';
import {queryClientAtom} from 'jotai-tanstack-query';
import React, {ReactNode, useMemo} from 'react';
import {Provider as ReduxProvider} from 'react-redux';
import {Action, Store as ReduxStore, bindActionCreators} from 'redux';
import {Logout$, logout} from '~/entities/user';
import {LogoutNow$, LogoutNowEverywhere$, logoutNowEverywhere} from '~/entities/user/model/auth/authActions';
import {DialogsRegistry, DialogsRegistryProvider} from '~/library/ui-kit';
import {CaptchaProvider} from '../entities/captcha';
import AbilityContainer from './access-control/AbilityContainer';
import {MenuProvider} from './menu/MenuProvider';

type JotaiStore = ReturnType<typeof createStore>;

interface HttpClients {
    httpClient: HttpClient;
    httpClientPublic: HttpClient;
    uploadClient: UploadClient;
}

interface AppRequirements extends HttpClients {
    children?: ReactNode;
    reduxStore: ReduxStore<AppModel, Action<string>>;
    jotaiStore: JotaiStore;
    queryClient: QueryClient;
    dialogs: DialogsRegistry;
}

export const AppProviders = ({children, ...props}: AppRequirements) => {
    const {
        reduxStore,
        jotaiStore,
        queryClient,
        httpClient,
        httpClientPublic,
        uploadClient,
        dialogs,
        //
    } = props;

    useMemo(() => {
        jotaiStore.set(queryClientAtom, () => queryClient);
        jotaiStore.set(HttpClient$, () => httpClient);
        jotaiStore.set(PublicHttpClient$, () => httpClientPublic);
        jotaiStore.set(UploadClient$, () => uploadClient);
        jotaiStore.set(Logout$, () => bindActionCreators(logout, reduxStore.dispatch));
        jotaiStore.set(LogoutNow$, () => bindActionCreators(logout, reduxStore.dispatch));
        jotaiStore.set(LogoutNowEverywhere$, () => bindActionCreators(logoutNowEverywhere, reduxStore.dispatch));
    }, [jotaiStore]);

    /* eslint-disable react/jsx-key */
    return [
        <ReduxProvider store={reduxStore} children={null} stabilityCheck='never' />,

        <QueryClientProvider client={queryClient} />,

        <JotaiProvider store={jotaiStore} />,

        <CaptchaProvider />,

        <AbilityContainer />,

        <DialogsRegistryProvider registry={dialogs} />,

        <MenuProvider />,
    ].reduceRight((element, provider) => <provider.type {...provider.props} children={element} />, children);
};
